import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import Select from 'react-select'
import { BiSolidImageAdd } from 'react-icons/bi'
import Button from '../../../shared/components/Button/Button'
import api from '../../../utils/api'
import SelectStyles from "../../../shared/Select/SelectStyle"
import Checkbox from "../../../shared/components/form/checkbox"
import Input from "../../../shared/components/form/input"
import Textarea from "../../../shared/components/form/textarea"
import useFlashMessage from '../../../hooks/useFlashMessage'
import FormLayout from "../../../shared/components/FormLayout/FormLayout"
import RequiredMark from "../../../shared/components/form/RequiredMark"
import FormSection from "../../../shared/components/FormLayout/FormSection"
import ConfirmDeleteModal from "../../../shared/components/ConfirmDeleteModal/ConfirmDeleteModal"
import AddPhotoModal from "../../../shared/components/AddPhotoModal/AddPhotoModal"
import PhotoGallery from "../../../shared/components/PhotoGallery/PhotoGallery"
import "./FormGenerator.css"

function FormGenerator({ handleSubmit, generatorData, btnText, btCancelLink, btCancelAction, title }) {

    const [generator, setGenerator] = useState(generatorData || {})
    const [regions, setRegions] = useState([])
    const [token] = useState(localStorage.getItem('token') || '')
    const { setFlashMessage } = useFlashMessage()

    const [modalPhotoForm, setModalPhotoForm] = useState(false)
    const [photoBeingEdited, setPhotoBeingEdited] = useState(null)
    const [modalDeletePhoto, setModalDeletePhoto] = useState(false)
    const [photoToDelete, setPhotoToDelete] = useState(null)

    useEffect(() => {
        api.get('/region/getall', {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` }
        }).then((response) => {
            setRegions(response.data.regions)
        })
    }, [token])

    async function savePhoto(file, description) {
        const isEditing = !!photoBeingEdited

        if (!isEditing && !file) {
            setFlashMessage('Selecione uma foto antes de adicionar.', 'error')
            return
        }

        const formData = new FormData()
        if (file) formData.append('photo', file)
        formData.append('description', description)

        const url = isEditing
            ? `generator/photo/${generator._id}/${photoBeingEdited._id}`
            : `generator/photo/${generator._id}`

        let msgType = 'success'
        const data = await api[isEditing ? 'patch' : 'post'](url, formData, {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => response.data)
            .catch((err) => { msgType = 'error'; return err.response.data })

        setFlashMessage(data.message, msgType)
        if (msgType !== 'error') {
            setGenerator((prev) => ({ ...prev, photos: data.generator.photos }))
            setModalPhotoForm(false)
            setPhotoBeingEdited(null)
        }
    }

    function openAddPhoto() {
        setPhotoBeingEdited(null)
        setModalPhotoForm(true)
    }

    function openEditPhoto(photo) {
        setPhotoBeingEdited(photo)
        setModalPhotoForm(true)
    }

    async function removePhoto(photoId) {
        let msgType = 'success'
        const data = await api.delete(`generator/photo/${generator._id}/${photoId}`, {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => response.data)
            .catch((err) => { msgType = 'error'; return err.response.data })

        setFlashMessage(data.message, msgType)
        if (msgType !== 'error') {
            setGenerator((prev) => ({ ...prev, photos: (prev.photos || []).filter((p) => p._id !== photoId) }))
        }
        setModalDeletePhoto(false)
        setPhotoToDelete(null)
    }

    function confirmRemovePhoto(photo) {
        setPhotoToDelete(photo)
        setModalDeletePhoto(true)
    }

    function handleChange(e) {
        setGenerator({ ...generator, [e.target.name]: e.target.value })
    }

    function handleChangeCheckbox(name, value) {
        setGenerator({ ...generator, [name]: value })
    }

    const handleSelectChange = (selectedOption) => {
        setGenerator({ ...generator, [selectedOption.name]: selectedOption.value })
    }

    function submit(e) {
        e.preventDefault()
        handleSubmit(generator)
    }

    const optionsRegion = regions
        ? regions.map((item) => ({ name: "region", value: item._id, label: item.regionName }))
        : []

    function selectValue(id, array) {
        if (!array) return ""
        return array.find(i => i._id === id) || ""
    }

    return (
        <>
        <FormLayout requiredLegend
            title={title}
            onSubmit={submit}
            actions={
                <>
                    <Button variant="save" type="submit">{btnText || 'Salvar'}</Button>
                    {btCancelLink
                        ? <Link className="link-default" to={btCancelLink}><Button variant="cancel">Cancelar</Button></Link>
                        : btCancelAction && <Button variant="cancel" onClick={btCancelAction}>Cancelar</Button>
                    }
                </>
            }
        >
            {/* ── Identificação ── */}
            <FormSection label="Identificação">
                <div className="fl-row">
                    <div className="fl-col-wide form-group">
                        <Input
                            required text="Nome do gerador"
                            type="text"
                            name="name"
                            placeholder="Digite o nome do gerador"
                            handleOnChange={handleChange}
                            value={generator.name || ''}
                        />
                    </div>
                    <div className="fl-col-mid form-group">
                        <label className="input-label">Região:<RequiredMark /></label>
                        <Select
                            options={optionsRegion}
                            onChange={handleSelectChange}
                            styles={SelectStyles}
                            menuPortalTarget={document.body}
                            value={selectValue(generator.region, regions)}
                        />
                    </div>
                </div>
                <div className="fl-row">
                    <div className="fl-col-mid form-group">
                        <Input required text="Número de Série" type="text" name="serialNumber" handleOnChange={handleChange} value={generator.serialNumber || ''} />
                    </div>
                    <div className="fl-col-mid form-group">
                        <Input required text="Número de Patrimônio" type="text" name="patrimonyNumber" handleOnChange={handleChange} value={generator.patrimonyNumber || ''} />
                    </div>
                </div>
                <div className="form-group">
                    <Textarea text="Descrição" name="description" placeholder="Descrição do equipamento" handleOnChange={handleChange} value={generator.description || ''} />
                </div>
                <div className="form-group">
                    <Checkbox
                        text="Gerador desabilitado"
                        name="disabled"
                        handleOnChange={() => handleChangeCheckbox("disabled", !generator.disabled)}
                        check={generator.disabled || false}
                    />
                </div>
            </FormSection>

            {/* ── Especificações Técnicas ── */}
            <FormSection label="Especificações Técnicas">
                <div className="fl-row">
                    <div className="fl-col-mid form-group">
                        <Input required text="Fabricante" type="text" name="manufacturer" handleOnChange={handleChange} value={generator.manufacturer || ''} />
                    </div>
                    <div className="fl-col-mid form-group">
                        <Input text="Motor" type="text" name="engine" handleOnChange={handleChange} value={generator.engine || ''} />
                    </div>
                    <div className="fl-col-narrow form-group">
                        <Input required text="Potência" type="text" name="power" handleOnChange={handleChange} value={generator.power || ''} />
                    </div>
                    <div className="fl-col-narrow form-group">
                        <Input text="Capacidade do Tanque" type="text" name="tankCapacity" handleOnChange={handleChange} value={generator.tankCapacity || ''} />
                    </div>
                    <div className="fl-col-narrow form-group">
                        <Input text="Bateria" type="text" name="battery" handleOnChange={handleChange} value={generator.battery || ''} />
                    </div>
                    <div className="fl-col-mid form-group">
                        <Input required text="Alternador" type="text" name="alternator" handleOnChange={handleChange} value={generator.alternator || ''} />
                    </div>
                    <div className="fl-col-mid form-group">
                        <Input text="Sistema de Injeção" type="text" name="injectionSystem" handleOnChange={handleChange} value={generator.injectionSystem || ''} />
                    </div>
                    <div className="fl-col-mid form-group">
                        <Input text="Sistema de Arrefecimento" type="text" name="coolingSystem" handleOnChange={handleChange} value={generator.coolingSystem || ''} />
                    </div>
                    <div className="fl-col-mid form-group">
                        <Input text="Painel" type="text" name="panel" handleOnChange={handleChange} value={generator.panel || ''} />
                    </div>
                    <div className="fl-col-narrow form-group">
                        <Input text="QTA" type="text" name="qta" handleOnChange={handleChange} value={generator.qta || ''} />
                    </div>
                </div>
            </FormSection>

            {/* ── Filtros e Consumíveis ── */}
            <FormSection label="Filtros e Consumíveis">
                <div className="fl-row">
                    <div className="fl-col-mid form-group">
                        <Input text="Ar Primário" type="text" name="primaryAir" handleOnChange={handleChange} value={generator.primaryAir || ''} />
                    </div>
                    <div className="fl-col-mid form-group">
                        <Input text="Ar Secundário" type="text" name="secundaryAir" handleOnChange={handleChange} value={generator.secundaryAir || ''} />
                    </div>
                    <div className="fl-col-mid form-group">
                        <Input text="Comb. Primária" type="text" name="primaryComb" handleOnChange={handleChange} value={generator.primaryComb || ''} />
                    </div>
                    <div className="fl-col-mid form-group">
                        <Input text="Comb. Secundária" type="text" name="secundaryComb" handleOnChange={handleChange} value={generator.secundaryComb || ''} />
                    </div>
                    <div className="fl-col-mid form-group">
                        <Input text="Filtro de Água" type="text" name="waterFilter" handleOnChange={handleChange} value={generator.waterFilter || ''} />
                    </div>
                    <div className="fl-col-mid form-group">
                        <Input text="Lubrificante" type="text" name="lubrificant" handleOnChange={handleChange} value={generator.lubrificant || ''} />
                    </div>
                    <div className="fl-col-mid form-group">
                        <Input text="Óleo" type="text" name="oil" handleOnChange={handleChange} value={generator.oil || ''} />
                    </div>
                    <div className="fl-col-mid form-group">
                        <Input text="Racor" type="text" name="racor" handleOnChange={handleChange} value={generator.racor || ''} />
                    </div>
                    <div className="fl-col-mid form-group">
                        <Input text="Mangueira" type="text" name="hose" handleOnChange={handleChange} value={generator.hose || ''} />
                    </div>
                    <div className="fl-col-mid form-group">
                        <Input text="Correias" type="text" name="straps" handleOnChange={handleChange} value={generator.straps || ''} />
                    </div>
                </div>
            </FormSection>

            {/* ── Fotos ── */}
            <FormSection label="Fotos">
                {generator._id ? (
                    <>
                        <div className="gen-photo-header">
                            <Button variant="new" type="button" icon={BiSolidImageAdd} onClick={openAddPhoto}>Adicionar Foto</Button>
                        </div>
                        <PhotoGallery
                            photos={generator.photos}
                            onEdit={openEditPhoto}
                            onRemove={confirmRemovePhoto}
                        />
                    </>
                ) : (
                    <p className="gen-photo-empty">Salve o gerador primeiro para poder adicionar fotos.</p>
                )}
            </FormSection>

        </FormLayout>

        <AddPhotoModal
            modalStatus={modalPhotoForm}
            changeStatus={(status) => { setModalPhotoForm(status); if (!status) setPhotoBeingEdited(null) }}
            onSave={savePhoto}
            photo={photoBeingEdited}
        />

        <ConfirmDeleteModal
            modalStatus={modalDeletePhoto}
            changeStatus={setModalDeletePhoto}
            onConfirm={() => removePhoto(photoToDelete._id)}
            message="Deseja realmente excluir essa foto?"
            itemName={photoToDelete?.description}
        />
        </>
    )
}

export default FormGenerator
